/**
 * @module Local
 * @description
 */
import Head from "next/head";
import Link from "next/link";
import Tooltip from "@/blocks/Tooltip";

// Template
import Template from "@/templates/Default";

// Blocks
import HeroAddressCheck from "@/blocks/Heroes/HeroAddressCheck";
import ServicesValueProps from "@/blocks/Services/ServicesValueProps";
import ColumnContent from "@/blocks/ColumnContent";
import ColumnContentLocal from "@/blocks/ColumnContent/ColumnContentLocal";
import SplitSection from "@/blocks/SplitSection";
import BannerServiceCheckSingleLine from "@/blocks/Banners/BannerServiceCheckSingleLine";

/**
 * @function Page
 * @description
 */
const Page = () => (
  <>
    <Head>
      <title>Frontier Address Check | Internet Coverage and Service</title>
      <meta
        name="description"
        content="Discover if you're eligible for fiber internet with our Frontier address check. Enter your address now to see if you qualify for internet service today!"
      />
      <link rel="canonical" href="https://frontier.com/local" />
    </Head>

    <Template
      title="Check Frontier Availability to Find Internet Near You"
      hero={
        <HeroAddressCheck
          headline={
            <>
              <span key="span-1" className="u-color-blue">
                Frontier<sup key="sup-1">&reg;</sup>
              </span>{" "}
              Availability
            </>
          }
          subHeadline="We're expanding our 99.9% reliable network."
          useSelectAreasLegal
        />
      }
      primary={
        <>
          <ServicesValueProps />

          <ColumnContentLocal
            headline="Where does Frontier offer service?"
            copy="Check to see if your state is covered and if there's internet service available for you."
          />

          <BannerServiceCheckSingleLine theme="blue" />

          <SplitSection
            title="We’re here to make internet easy"
            subtitle="Learn about the perks of choosing Frontier as your Internet and Home Phone provider."
            theme="dark"
            image={{
              url: "/pages/local/gamer.jpg",
              blurUrl: "/pages/local/gamer.jpg",
              alt: "Gamer Boy",
            }}
            componentList={[
              {
                headline: "No data caps",
                subhead: (
                  <>
                    With Frontier internet service, you wont have to work around
                    data limits. Use as much data as you want with no extra
                    fees.
                    <Tooltip disclosure="data-caps" />
                  </>
                ),
              },
              {
                headline: "Wi-Fi router included",
                subhead:
                  "Leave the equipment to us. Get state-of-the-art Wi-Fi router equipment included in your monthly plan.",
              },
            ]}
            reverseLayout
          />

          <ColumnContent
            headline="Frontier Internet Services"
            copy="Learn more about Frontier availability near you."
            items={[
              {
                headline: "Frontier Fiber Internet",
                content: (
                  <>
                    Game, stream and work with 99.9% network reliability. Learn
                    more about{" "}
                    <Link
                      key="link-1"
                      href="/shop/internet/fiber-internet"
                      aria-label="Link to fiber internet page"
                    >
                      Frontier Fiber
                    </Link>{" "}
                    availability near you by checking your address.
                  </>
                ),
              },
              {
                headline: "High-Speed Internet",
                content: (
                  <>
                    <Link
                      key="link-2"
                      href="/shop/internet"
                      aria-label="Link to shop internet page"
                    >
                      Frontier Internet
                    </Link>{" "}
                    is designed so everyday internet users can connect with
                    their world without worrying about data caps.
                    <Tooltip disclosure="data-caps" />
                  </>
                ),
              },
              {
                headline: "Bundle Internet and Home Phone",
                content: (
                  <>
                    When you bundle your Frontier Internet service, you maximize
                    your value and convenience.{" "}
                    <Link
                      key="link-3"
                      href="/shop"
                      aria-label="Link to shop page"
                    >
                      Explore the benefits of bundling today.
                    </Link>
                  </>
                ),
              },
            ]}
          />
        </>
      }
    ></Template>
  </>
);

export default Page;
